import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL || ''}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token and workspace context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('line_rich_menu_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const workspaceId = localStorage.getItem('selectedWorkspaceId');
    if (workspaceId) {
      config.headers['X-Workspace-ID'] = workspaceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data on 401
      localStorage.removeItem('line_rich_menu_token');
      localStorage.removeItem('line_rich_menu_user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Image upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Channel CRUD operations
export const createChannel = async (channelData) => {
  const response = await api.post('/channels', channelData);
  return response.data;
};

export const getChannels = async () => {
  const response = await api.get('/channels');
  return response.data;
};

export const getChannel = async (channelId) => {
  const response = await api.get(`/channels/${channelId}`);
  return response.data;
};

export const updateChannel = async (channelId, channelData) => {
  const response = await api.put(`/channels/${channelId}`, channelData);
  return response.data;
};

export const deleteChannel = async (channelId) => {
  const response = await api.delete(`/channels/${channelId}`);
  return response.data;
};

export const validateChannel = async (channelId) => {
  const response = await api.post(`/channels/${channelId}/validate`);
  return response.data;
};

export const getChannelBotInfo = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/bot-info`);
  return response.data;
};

// Menu CRUD operations
export const createMenu = async (menuData) => {
  const response = await api.post('/menus', menuData);
  return response.data;
};

export const getMenus = async (channelId = null) => {
  const params = channelId ? { channel_id: channelId } : {};
  const response = await api.get('/menus', { params });
  return response.data;
};

export const getMenu = async (menuId) => {
  const response = await api.get(`/menus/${menuId}`);
  return response.data;
};

export const updateMenu = async (menuId, menuData) => {
  const response = await api.put(`/menus/${menuId}`, menuData);
  return response.data;
};

export const deleteMenu = async (menuId) => {
  const response = await api.delete(`/menus/${menuId}`);
  return response.data;
};

export const publishMenu = async (menuId) => {
  const response = await api.post(`/menus/${menuId}/publish`);
  return response.data;
};

export const setDefaultMenu = async (menuId) => {
  const response = await api.post(`/menus/${menuId}/set-default`);
  return response.data;
};

export const unsetDefaultMenu = async (channelId) => {
  const response = await api.delete(`/channels/${channelId}/default-menu`);
  return response.data;
};

export const getDefaultMenu = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/default-menu`);
  return response.data;
};

export const duplicateMenu = async (menuId, targetChannelId = null, options = {}) => {
  const body = { ...(targetChannelId ? { targetChannelId } : {}), ...options };
  const response = await api.post(`/menus/${menuId}/duplicate`, body);
  return response.data;
};

// Campaigns
export const getCampaignsConfig = async () => {
  const response = await api.get('/campaigns/config');
  return response.data;
};

export const getCampaigns = async (channelId) => {
  const response = await api.get('/campaigns', { params: { channel_id: channelId } });
  return response.data;
};

export const createCampaign = async (body) => {
  const response = await api.post('/campaigns', body);
  return response.data;
};

export const getCampaign = async (id) => {
  const response = await api.get(`/campaigns/${id}`);
  return response.data;
};

export const updateCampaign = async (id, body) => {
  const response = await api.put(`/campaigns/${id}`, body);
  return response.data;
};

export const deleteCampaign = async (id) => {
  const response = await api.delete(`/campaigns/${id}`);
  return response.data;
};

export const checkCampaign = async (id) => {
  const response = await api.post(`/campaigns/${id}/check`);
  return response.data;
};

export const scheduleCampaign = async (id, localDateTime, timeZone) => {
  const response = await api.post(`/campaigns/${id}/schedule`, { localDateTime, timeZone });
  return response.data;
};

export const unscheduleCampaign = async (id) => {
  const response = await api.post(`/campaigns/${id}/unschedule`);
  return response.data;
};

export const launchCampaign = async (id, confirmChannelName) => {
  const response = await api.post(`/campaigns/${id}/launch`, { confirmChannelName });
  return response.data;
};

export const rollbackCampaign = async (id, confirmChannelName) => {
  const response = await api.post(`/campaigns/${id}/rollback`, { confirmChannelName });
  return response.data;
};

export const getCampaignRuns = async (id) => {
  const response = await api.get(`/campaigns/${id}/runs`);
  return response.data;
};

export const getCampaignRun = async (runId) => {
  const response = await api.get(`/campaign-runs/${runId}`);
  return response.data;
};

// Rich Menu Aliases
export const createMenuAlias = async (menuId, aliasId) => {
  const response = await api.post(`/menus/${menuId}/alias`, { richMenuAliasId: aliasId });
  return response.data;
};

export const deleteMenuAlias = async (menuId) => {
  const response = await api.delete(`/menus/${menuId}/alias`);
  return response.data;
};

// LINE API (channel-specific)
export const getLineMenus = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/line/menus`);
  return response.data;
};

export const getLineAliases = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/line/aliases`);
  return response.data;
};

export const getChannelLineStatus = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/line-status`);
  return response.data;
};

export const getLineMenuDetail = async (channelId, richMenuId) => {
  const response = await api.get(`/channels/${channelId}/line/menus/${richMenuId}`);
  return response.data;
};

export const deleteLineMenus = async (channelId, richMenuIds) => {
  const response = await api.delete(`/channels/${channelId}/line/menus`, { data: { richMenuIds } });
  return response.data;
};

export const getChannelVisitors = async (channelId) => {
  const response = await api.get(`/channels/${channelId}/visitors`);
  return response.data;
};

export const getUserMenu = async (channelId, lineUserId) => {
  const response = await api.get(`/channels/${channelId}/user-menu/${lineUserId}`);
  return response.data;
};

export const clearUserMenu = async (channelId, lineUserId) => {
  const response = await api.delete(`/channels/${channelId}/user-menu/${lineUserId}`);
  return response.data;
};

// Interest / Pro requests (public endpoint, also used by the landing page)
export const submitInterest = async (data) => {
  const response = await api.post('/interest', data);
  return response.data;
};

// Auth operations
export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateCurrentUser = async (userData) => {
  const response = await api.put('/auth/me', userData);
  return response.data;
};

// User Management (Admin only)
export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const getUser = async (userId) => {
  const response = await api.get(`/users/${userId}`);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

export const assignChannelToUser = async (userId, channelId) => {
  const response = await api.post(`/users/${userId}/channels/${channelId}`);
  return response.data;
};

export const removeChannelFromUser = async (userId, channelId) => {
  const response = await api.delete(`/users/${userId}/channels/${channelId}`);
  return response.data;
};

// Admin Stats
export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

// Admin Activity Logs
export const getActivityLogs = async (params = {}) => {
  const response = await api.get('/admin/activity-logs', { params });
  return response.data;
};

export const getWorkspaceActivityLogs = async (workspaceId, params = {}) => {
  const response = await api.get(`/workspaces/${workspaceId}/activity-logs`, { params });
  return response.data;
};

// Workspace operations
export const getWorkspaces = async () => {
  const response = await api.get('/workspaces');
  return response.data;
};

export const createWorkspace = async (data) => {
  const response = await api.post('/workspaces', data);
  return response.data;
};

export const updateWorkspace = async (id, data) => {
  const response = await api.put(`/workspaces/${id}`, data);
  return response.data;
};

// Platform admin: which features each plan includes
export const getPlanFeatures = async () => {
  const response = await api.get('/admin/features');
  return response.data;
};

export const updatePlanFeatures = async (planFeatures) => {
  const response = await api.put('/admin/features', { planFeatures });
  return response.data;
};

export const deleteWorkspace = async (id) => {
  const response = await api.delete(`/workspaces/${id}`);
  return response.data;
};

export const getWorkspaceMembers = async (workspaceId) => {
  const response = await api.get(`/workspaces/${workspaceId}/members`);
  return response.data;
};

export const addWorkspaceMember = async (workspaceId, data) => {
  const response = await api.post(`/workspaces/${workspaceId}/members`, data);
  return response.data;
};

export const updateWorkspaceMember = async (workspaceId, userId, data) => {
  const response = await api.put(`/workspaces/${workspaceId}/members/${userId}`, data);
  return response.data;
};

export const removeWorkspaceMember = async (workspaceId, userId) => {
  const response = await api.delete(`/workspaces/${workspaceId}/members/${userId}`);
  return response.data;
};

export const getWorkspaceInvites = async (workspaceId) => {
  const response = await api.get(`/workspaces/${workspaceId}/invites`);
  return response.data;
};

export const cancelWorkspaceInvite = async (workspaceId, inviteId) => {
  const response = await api.delete(`/workspaces/${workspaceId}/invites/${inviteId}`);
  return response.data;
};

export const resendWorkspaceInvite = async (workspaceId, inviteId) => {
  const response = await api.post(`/workspaces/${workspaceId}/invites/${inviteId}/resend`);
  return response.data;
};

// Invites
export const getInvite = async (token) => {
  const response = await api.get(`/invites/${token}`);
  return response.data;
};

export const acceptInvite = async (token) => {
  const response = await api.post(`/invites/${token}/accept`);
  return response.data;
};

export const setupAndAcceptInvite = async (token, data) => {
  const response = await api.post(`/invites/${token}/setup`, data);
  return response.data;
};

export default api;
